import { useState } from 'react'
import { Loader2, UserPlus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useAgentInvitations, useAgentStats } from '@/api/team'
import { InviteAgentDialog } from './team/InviteAgentDialog'
import { AgentsList } from './team/AgentsList'
import { InvitationsList } from './team/InvitationsList'
import { TeamGroupsSection } from './team/TeamGroupsSection'
import { InternalCallsRecordingSection } from './team/InternalCallsRecordingSection'
import { RecordingAccessSection } from './team/RecordingAccessSection'

type TeamTab = 'members' | 'invitations'

export function TeamView() {
  const { t } = useTranslation()
  const [inviteOpen, setInviteOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<TeamTab>('members')
  const { data: stats, isLoading: statsLoading } = useAgentStats()
  const { data: invitations = [], isLoading: invLoading } = useAgentInvitations()

  if (statsLoading || invLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <p className="text-sm text-slate-500">
          {t('manager.team.loadError')}
        </p>
      </div>
    )
  }

  const members = stats.interface_agents
  const memberCount = members.length
  const invitationCount = invitations.length
  const canInvite = stats.can_add_interface_agent

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-6">
            <h1 className="min-w-0 text-2xl font-semibold text-slate-900 dark:text-white">
              {t('manager.team.sectionTitle')}
            </h1>
            <Button
              type="button"
              onClick={() => setInviteOpen(true)}
              disabled={!canInvite}
              className="bg-[#0A192F] text-white hover:bg-[#0A192F]/90 dark:bg-white dark:text-[#0A192F] dark:hover:bg-white/90 flex-shrink-0 self-start"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              {t('manager.team.inviteCta')}
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-2 mb-2 border-b border-slate-200 dark:border-slate-800">
            <TabButton
              active={activeTab === 'members'}
              onClick={() => setActiveTab('members')}
            >
              {t('manager.team.tabs.members')}{' '}
              <span
                className={cn(
                  'ml-1 text-xs font-normal',
                  activeTab === 'members'
                    ? 'text-slate-500 dark:text-slate-400'
                    : 'text-slate-400 dark:text-slate-500',
                )}
              >
                ({memberCount})
              </span>
            </TabButton>
            <TabButton
              active={activeTab === 'invitations'}
              onClick={() => setActiveTab('invitations')}
            >
              {t('manager.team.tabs.pendingInvitations')}{' '}
              <span
                className={cn(
                  'ml-1 text-xs font-normal',
                  activeTab === 'invitations'
                    ? 'text-slate-500 dark:text-slate-400'
                    : 'text-slate-400 dark:text-slate-500',
                )}
              >
                ({invitationCount})
              </span>
            </TabButton>
          </div>

          {!canInvite && activeTab === 'members' && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800/50 p-3 mt-4 text-sm text-amber-800 dark:text-amber-200">
              {t('manager.team.limitReached', {
                max: stats.max_interface_agents,
              })}
            </div>
          )}

          <div className="pt-4">
            {activeTab === 'members' ? (
              <>
                <AgentsList
                  agents={members}
                  onInvite={() => setInviteOpen(true)}
                />
                <RecordingAccessSection />
                <TeamGroupsSection />
                <InternalCallsRecordingSection />
              </>
            ) : (
              <InvitationsList invitations={invitations} />
            )}
          </div>
        </div>
      </div>

      <InviteAgentDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
      />
    </div>
  )
}

interface TabButtonProps {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}

function TabButton({ active, onClick, children }: TabButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative px-4 py-2 text-sm font-medium transition-colors -mb-px border-b-2',
        active
          ? 'text-slate-900 dark:text-white border-slate-900 dark:border-white'
          : 'text-slate-500 dark:text-slate-400 border-transparent hover:text-slate-700 dark:hover:text-slate-200',
      )}
    >
      {children}
    </button>
  )
}
